import React, { useState, useCallback } from "react";
import "./AdminPortal.css";

const API_BASE = "https://api-2026.fikacreatives.co";
const LOGO =
  "https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/sign/landing%20page/blue%20fika%20logo.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZGI0MGU3NC04Nzk2LTRhZmItOTViNy0wNDQ5YzAyNWMzZjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsYW5kaW5nIHBhZ2UvYmx1ZSBmaWthIGxvZ28ucG5nIiwiaWF0IjoxNzc2ODE2ODMwLCJleHAiOjE4MDgzNTI4MzB9.H5RlV1zB9mj_TES7gtHRYO0bphEZOcF30yR96p5s6SY";

function Toast({ message, isError, onDone }) {
  React.useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className={`admin-toast${isError ? " admin-toast-error" : ""}`}>
      {message}
    </div>
  );
}

export default function AdminCycles() {
  const [authed, setAuthed] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [token, setToken] = useState("");

  const [cycleAvailData, setCycleAvailData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, isError = false) => {
    setToast({ message, isError, key: Date.now() });
  };

  const fetchCycles = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/match-cycles`, {
        headers: { Authorization: `Basic ${tok}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load cycle data");
      setCycleAvailData(data.grouped || {});
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setLoading(false);
    }
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      const basicToken = btoa(`${username}:${password}`);
      const res = await fetch(`${API_BASE}/api/admin/people`, {
        headers: { Authorization: `Basic ${basicToken}` },
      });
      if (res.status === 401) {
        setAuthError("Invalid credentials.");
        return;
      }
      if (!res.ok) throw new Error("Login failed");
      setToken(basicToken);
      setAuthed(true);
      fetchCycles(basicToken);
    } catch (e) {
      setAuthError(e.message);
    } finally {
      setAuthLoading(false);
    }
  }

  if (!authed) {
    return (
      <div className="admin-page admin-login">
        <div className="admin-login-card">
          <img src={LOGO} alt="Fika" className="admin-login-logo" />
          <h2>Admin Portal</h2>
          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button className="admin-login-btn" type="submit" disabled={authLoading}>
              {authLoading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          {authError && <p className="admin-login-error">{authError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <img src={LOGO} alt="Fika" className="admin-header-logo" />
      </header>

      <div className="admin-cycles-section">
        <div className="admin-cycles-header">
          <h2 className="admin-cycles-title">Cycle Availability</h2>
          <button
            className="admin-refresh-btn"
            onClick={() => fetchCycles(token)}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {loading ? (
          <div className="admin-loading" style={{ minHeight: 80 }}>Loading cycle data…</div>
        ) : cycleAvailData === null ? null : Object.keys(cycleAvailData).length === 0 ? (
          <p className="admin-cycles-empty">No match cycles found.</p>
        ) : (
          Object.entries(cycleAvailData).map(([city, cycles]) => (
            <div key={city} className="admin-city-cycles">
              {cycles.map((cycle) => {
                const dateLabel = (() => {
                  try {
                    return new Date(cycle.cycle_date + "T00:00:00").toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                    });
                  } catch {
                    return cycle.cycle_date;
                  }
                })();

                return (
                  <div key={cycle.cycle_id} className="admin-cycle-block">
                    <h3 className="admin-cycle-heading">{dateLabel} - {city}</h3>
                    <div className="admin-cycle-options">
                      {[1, 2, 3].map((opt) => (
                        <div key={opt} className="admin-cycle-card">
                          <div className="admin-cycle-card-label">{opt}.</div>
                          {(cycle.options[opt] || []).length === 0 ? (
                            <div className="admin-cycle-empty">—</div>
                          ) : (
                            (cycle.options[opt] || []).map((p) => (
                              <div key={p.person_id} className="admin-cycle-person">
                                <span className="admin-cycle-person-name">{p.name}</span>
                                {p.phone && (
                                  <span className="admin-cycle-person-phone"> - {p.phone}</span>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      ))}
                      <div className="admin-cycle-card admin-cycle-card--cant">
                        <div className="admin-cycle-card-label">4. can't make it</div>
                        {(cycle.options[4] || []).length === 0 ? (
                          <div className="admin-cycle-empty">—</div>
                        ) : (
                          (cycle.options[4] || []).map((p) => (
                            <div key={p.person_id} className="admin-cycle-person">
                              <span className="admin-cycle-person-name">{p.name}</span>
                              {p.phone && (
                                <span className="admin-cycle-person-phone"> - {p.phone}</span>
                              )}
                              {p.no_response && (
                                <span className="admin-cycle-no-response"> (no response)</span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {toast && (
        <Toast
          key={toast.key}
          message={toast.message}
          isError={toast.isError}
          onDone={() => setToast(null)}
        />
      )}
    </div>
  );
}
